import { useProduct } from '@chordcommerce/react-autonomy';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import InputWithButton from '~/components/Generic/Form/InputWithButton';
import { serializeProduct } from '~/utils/serialize';
import { Step } from '../ProductCard';
import { Text } from 'theme-ui';
import { useCustomAnalytics } from '~/hooks/utils/customAnalytics';

type FormValues = {
  email: string;
};

type Props = {
  product: Queries.ProductCardFragmentFragment;
  selectedVariant: Queries.VariantDetailsFragmentFragment;
  setCurrentStep: React.Dispatch<React.SetStateAction<Step>>;
};

const StockRequestForm = ({ product, selectedVariant, setCurrentStep }: Props) => {
  const [isCreatingStockRequest, setIsCreatingStockRequest] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const { register, handleSubmit } = useForm<FormValues>();
  const { createStockRequest } = useProduct();
  const { trackEmailCaptured } = useCustomAnalytics();
  const onSubmit: SubmitHandler<FormValues> = async (data: { email: string }) => {
    setIsCreatingStockRequest(true);
    try {
      await createStockRequest({ email: data.email, product: serializeProduct(product, selectedVariant?.sku ?? '') });
      await trackEmailCaptured({
        email: data.email,
        locationDetail: 'stock_request_quickshop',
      });
      setSuccess(true);
      timeoutId.current = setTimeout(() => {
        setCurrentStep('variant');
      }, 4000);
    } catch (error) {
      setError(true);
      console.error(error);
    } finally {
      setIsCreatingStockRequest(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await onSubmit({ email: e.currentTarget.value });
    }
  };
  return (
    <>
      {success ? (
        <Text
          sx={{
            fontSize: '1.4rem',
          }}
        >
          Thanks! We'll let you know when this product is back in stock.
        </Text>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
          <InputWithButton
            // @ts-ignore
            isLoading={isCreatingStockRequest}
            buttonText="Notify me"
            placeholder="Enter your email address"
            forwardSx={{ width: '100%' }}
            onKeyDown={handleKeyDown}
            {...register('email', {
              required: true,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'invalid email address',
              },
            })}
          />
          {error && (
            <Text
              sx={{
                fontSize: '1.4rem',
                color: 'red',
              }}
            >
              An unexpected error occurred.
            </Text>
          )}
        </form>
      )}
    </>
  );
};

export default StockRequestForm;
